import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  StatusBadgeRendererComponent,
  AvatarRendererComponent,
  ActionButtonsRendererComponent,
  ActionButton,
} from 'app/shared/components/smart-grid';
import { MOCK_ORDERS, MockOrder } from '../mock-data/mock-orders.data';

/**
 * Cell Custom Components Example
 * Demonstrates custom cell renderers for advanced cell formatting
 */
@Component({
  selector: 'app-cell-renderers-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Renderizadores de Celdas Personalizados</h2>
        <p class="text-gray-600 mb-2">
          El Smart Grid soporta renderizadores personalizados de celdas para formatos avanzados como badges, avatares, botones de acción y más.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            🎨 Custom Renderers
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            🏷️ Status Badges
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            👤 Avatars
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            🎬 Action Buttons
          </span>
        </div>
      </div>

      <!-- Action Log -->
      <div *ngIf="actionLog.length > 0" class="mb-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2 flex items-center gap-2">
          <mat-icon class="text-blue-600">info</mat-icon>
          Última Acción:
        </h3>
        <p class="text-sm">{{ actionLog[actionLog.length - 1] }}</p>
      </div>

      <!-- Grid with Custom Cell Renderers -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          [data]="gridData"
          [columnDefs]="columnDefs"
          [config]="gridConfig">
        </app-smart-grid>
      </div>

      <!-- Features List -->
      <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="p-4 bg-purple-50 dark:bg-purple-900 rounded">
          <h4 class="font-semibold flex items-center gap-2 mb-2">
            <mat-icon class="text-purple-600">badge</mat-icon>
            Status Badge Renderer
          </h4>
          <ul class="text-sm space-y-1">
            <li>✓ Mapeo de estados a labels</li>
            <li>✓ Colores personalizables</li>
            <li>✓ Soporte dark mode</li>
            <li>✓ Badges redondeados</li>
          </ul>
        </div>

        <div class="p-4 bg-green-50 dark:bg-green-900 rounded">
          <h4 class="font-semibold flex items-center gap-2 mb-2">
            <mat-icon class="text-green-600">account_circle</mat-icon>
            Avatar Renderer
          </h4>
          <ul class="text-sm space-y-1">
            <li>✓ Avatares con iniciales</li>
            <li>✓ Soporte para imágenes</li>
            <li>✓ Color generado por nombre</li>
            <li>✓ Tamaños configurables</li>
          </ul>
        </div>

        <div class="p-4 bg-orange-50 dark:bg-orange-900 rounded">
          <h4 class="font-semibold flex items-center gap-2 mb-2">
            <mat-icon class="text-orange-600">touch_app</mat-icon>
            Action Buttons Renderer
          </h4>
          <ul class="text-sm space-y-1">
            <li>✓ Botones de acción múltiples</li>
            <li>✓ Callbacks onClick</li>
            <li>✓ Estados disabled/hidden</li>
            <li>✓ Tooltips configurables</li>
          </ul>
        </div>
      </div>

      <!-- Configuration Example -->
      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">⚙️ Configuración de Ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{configExample}}</code></pre>
      </div>

      <!-- Best Practices -->
      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">💡 Mejores Prácticas:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>Usa <strong>StatusBadgeRenderer</strong> para estados, prioridades y categorías</li>
          <li>Usa <strong>AvatarRenderer</strong> para usuarios, contactos y perfiles</li>
          <li>Usa <strong>ActionButtonsRenderer</strong> para acciones inline (ver, editar, eliminar)</li>
          <li>Crea tus propios renderers implementando <code>CellRenderer&lt;T&gt;</code> interface</li>
          <li>Los botones de acción deben detener la propagación del evento click del row</li>
          <li>Usa callbacks para manejar acciones desde el componente padre</li>
        </ul>
      </div>
    </div>
  `,
})
export class CellRenderersExampleComponent {
  gridData: MockOrder[] = MOCK_ORDERS.slice(0, 20);
  actionLog: string[] = [];

  // Action buttons configuration
  actionButtons: ActionButton[] = [
    {
      icon: 'visibility',
      tooltip: 'Ver detalles',
      color: 'primary',
      onClick: (data: MockOrder) => this.viewOrder(data),
    },
    {
      icon: 'edit',
      tooltip: 'Editar',
      color: 'accent',
      onClick: (data: MockOrder) => this.editOrder(data),
      disabled: (data: MockOrder) => data.statusId === 0, // Disabled for cancelled
    },
    {
      icon: 'delete',
      tooltip: 'Eliminar',
      color: 'warn',
      onClick: (data: MockOrder) => this.deleteOrder(data),
      hidden: (data: MockOrder) => data.statusId === 0, // Hidden for cancelled
    },
  ];

  // Column definitions with custom cell renderers
  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Orden #',
      width: 120,
      pinned: 'left',
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 200,
      cellRenderer: AvatarRendererComponent,
      cellRendererParams: {
        imageField: 'soldTo.avatar', // Doesn't exist in mock data, will show initials
        showName: true,
        size: 'md',
        colorSeed: true,
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toFixed(2)}`,
      cellClass: 'text-right font-semibold',
    },
    {
      field: 'statusId',
      headerName: 'Estado',
      width: 140,
      cellRenderer: StatusBadgeRendererComponent,
      cellRendererParams: {
        statusMap: {
          0: { label: 'Anulado', color: 'red' },
          1: { label: 'Activo', color: 'green' },
          6: { label: 'En Progreso', color: 'blue' },
          7: { label: 'Aprobado', color: 'purple' },
          8: { label: 'Rechazado', color: 'red' },
          18: { label: 'Inactivo', color: 'gray' },
        },
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 150,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'subRoute.name',
      headerName: 'Ruta',
      width: 180,
    },
    {
      headerName: 'Acciones',
      sortable: false,
      filterable: false,
      width: 150,
      cellRenderer: ActionButtonsRendererComponent,
      cellRendererParams: {
        buttons: this.actionButtons,
      },
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 10,
      pageSizeOptions: [10, 20, 50],
    },
    sorting: {
      enabled: true,
      multiColumn: false,
    },
    search: {
      enabled: true,
      showSearchInput: true,
      placeholder: 'Buscar órdenes...',
      searchableFields: ['serial', 'soldTo.name'],
    },
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
    },
    styling: {
      maxHeight: '600px',
    },
  };

  // Action handlers
  viewOrder(order: MockOrder): void {
    this.actionLog.push(`👁️ Ver detalles de orden: ${order.serial}`);
  }

  editOrder(order: MockOrder): void {
    this.actionLog.push(`✏️ Editar orden: ${order.serial}`);
  }

  deleteOrder(order: MockOrder): void {
    this.actionLog.push(`🗑️ Eliminar orden: ${order.serial}`);
  }

  configExample = `// Cell Custom Components Configuration
columnDefs: ColDef<Order>[] = [
  // Status Badge Renderer
  {
    field: 'statusId',
    headerName: 'Estado',
    cellRenderer: StatusBadgeRendererComponent,
    cellRendererParams: {
      statusMap: {
        0: { label: 'Anulado', color: 'red' },
        1: { label: 'Activo', color: 'green' },
        6: { label: 'En Progreso', color: 'blue' },
      }
    }
  },

  // Avatar Renderer
  {
    field: 'soldTo.name',
    headerName: 'Cliente',
    cellRenderer: AvatarRendererComponent,
    cellRendererParams: {
      imageField: 'soldTo.avatar',
      showName: true,
      size: 'md',
      colorSeed: true,
    }
  },

  // Action Buttons Renderer
  {
    field: 'actions',
    headerName: 'Acciones',
    sortable: false,
    cellRenderer: ActionButtonsRendererComponent,
    cellRendererParams: {
      buttons: [
        {
          icon: 'edit',
          tooltip: 'Editar',
          color: 'accent',
          onClick: (data) => this.edit(data),
          disabled: (data) => data.statusId === 0
        },
        {
          icon: 'delete',
          tooltip: 'Eliminar',
          color: 'warn',
          onClick: (data) => this.delete(data)
        }
      ]
    }
  }
];`;
}
